import { useEffect, useRef, type MouseEvent } from 'react';
import { Button } from '../../ui/Button';
import { nav } from '../../copy/shell';
import { NavItems } from './NavItems';
import { NavFooter } from './NavFooter';
import s from './NavDrawer.module.css';

/**
 * A tap on the backdrop closes the drawer. The backdrop is the dialog's own ::backdrop, so
 * the click lands on the <dialog> itself, at a point outside its box; a tap on the
 * drawer's own padding lands on it too, but inside, and is left alone. close() fires the
 * close event, which is what tells the shell. This is the drawer's alone: a shared Dialog
 * holds typed input or a one-time secret, and must never close by accident.
 */
function closeOnBackdrop(e: MouseEvent<HTMLDialogElement>) {
  if (e.target !== e.currentTarget) return;
  const r = e.currentTarget.getBoundingClientRect();
  if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) e.currentTarget.close();
}

/**
 * The nav on a phone.
 *
 * A native <dialog> rather than a styled div: showModal() brings the focus trap, the
 * inert background, Escape-to-close and the top layer with it, and every hand-rolled
 * version of that list is missing at least one of them.
 */
export function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Guarded both ways: showModal() on an open dialog throws InvalidStateError, and
    // React 19's strict mode runs this effect twice in development.
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog ref={ref} className={s.drawer} aria-label={nav.primary} onClose={onClose} onClick={closeOnBackdrop}>
      <div className={s.head}>
        <Button variant="ghost" size="md" onClick={onClose}>
          {nav.closeMenu}
        </Button>
      </div>
      <nav className={s.items} aria-label={nav.primary}>
        <NavItems onNavigate={onClose} />
      </nav>
      <div className={s.footer}>
        <NavFooter />
      </div>
    </dialog>
  );
}

export default NavDrawer;
